import { useCallback, useEffect, useState } from 'react';

import type { FundPolicy } from '../../../core/fundPolicy/schema';
import { EMPLOYEE_EDITABLE_STATUSES } from '../domain/declaration/schema';
import {
  splitAnswers,
  toAnswerMap,
  validateDeclaration,
  type AnswerMap,
  type AnswerValue,
} from './declarationForm';
import type { DeclarationPort, MyDeclaration } from './ports/declarationPort';

/**
 * Loads and edits the employee's own declaration (§5.4 — coordination only;
 * the rules are in `declarationForm.ts` and tested without rendering).
 */
export interface MyDeclarationController {
  readonly answers: AnswerMap;
  readonly missingItemIds: readonly string[];
  readonly missingLabelsAr: readonly string[];
  readonly submitted: boolean;
  readonly readOnly: boolean;
  readonly reviewNoteAr: string | null;
  readonly saving: boolean;
  readonly savedAr: string | null;
  readonly errorAr: string | null;
  readonly change: (itemId: string, value: AnswerValue) => void;
  readonly saveDraft: () => void;
  readonly submit: () => void;
}

export function useMyDeclaration(
  port: DeclarationPort,
  policy: FundPolicy | null,
  enabled: boolean,
): MyDeclarationController {
  const [declaration, setDeclaration] = useState<MyDeclaration | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [missing, setMissing] = useState<{
    ids: readonly string[];
    labels: readonly string[];
  }>({ ids: [], labels: [] });
  const [saving, setSaving] = useState(false);
  const [savedAr, setSavedAr] = useState<string | null>(null);
  const [errorAr, setErrorAr] = useState<string | null>(null);

  const policyYear = policy?.policyYear ?? null;

  useEffect(() => {
    if (!enabled || policyYear === null) return;

    let cancelled = false;
    port
      .getMyDeclaration(policyYear)
      .then((loaded) => {
        if (cancelled || !loaded) return;
        setDeclaration(loaded);
        // Both halves are merged back into one editing map. The split is a
        // storage concern; the employee answers one form.
        setAnswers({
          ...toAnswerMap(loaded.answers),
          ...toAnswerMap(loaded.medicalAnswers),
        });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر تحميل الإقرار.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [port, policyYear, enabled]);

  const change = useCallback((itemId: string, value: AnswerValue) => {
    setSavedAr(null);
    setAnswers((current) => ({ ...current, [itemId]: value }));
  }, []);

  const saveDraft = useCallback(() => {
    if (!policy) return;

    setSaving(true);
    setErrorAr(null);

    const { administrative, medical } = splitAnswers(policy, answers);
    void port
      .saveDraft(policy.policyYear, administrative, medical)
      .then(() => {
        setSavedAr('تم حفظ المسودة.');
      })
      .catch((error: unknown) => {
        setErrorAr(error instanceof Error ? error.message : 'تعذّر الحفظ.');
      })
      .finally(() => {
        setSaving(false);
      });
  }, [port, policy, answers]);

  const submit = useCallback(() => {
    if (!policy) return;

    const validation = validateDeclaration(policy, answers);
    setMissing({ ids: validation.missingItemIds, labels: validation.missingLabelsAr });
    if (!validation.valid) return;

    setSaving(true);
    setErrorAr(null);

    // Saved before submitting, so the stored answers can never lag behind the
    // status: a submitted declaration missing its last edit is unreviewable.
    const { administrative, medical } = splitAnswers(policy, answers);
    void port
      .saveDraft(policy.policyYear, administrative, medical)
      .then(() => port.submit())
      .then(() => {
        setDeclaration((current) =>
          current === null ? current : { ...current, status: 'submitted' },
        );
        setSavedAr('تم إرسال الإقرار.');
      })
      .catch((error: unknown) => {
        setErrorAr(error instanceof Error ? error.message : 'تعذّر الإرسال.');
      })
      .finally(() => {
        setSaving(false);
      });
  }, [port, policy, answers]);

  // Editability comes from the domain constant the security rules were written
  // against. Restating it here as `!== 'draft'` is how a screen and a rule
  // drift apart.
  const status = declaration?.status ?? 'draft';
  const submitted = !EMPLOYEE_EDITABLE_STATUSES.includes(status);

  return {
    answers,
    missingItemIds: missing.ids,
    missingLabelsAr: missing.labels,
    submitted,
    readOnly: submitted,
    reviewNoteAr: declaration?.reviewNoteAr ?? null,
    saving,
    savedAr,
    errorAr,
    change,
    saveDraft,
    submit,
  };
}
